import { BrowserRouter, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import type { ReactElement, ReactNode } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { IdentityProvider, type CoreIdentity } from '@core/contexts/IdentityContext';
import { ProfileProvider } from '@core/contexts/ProfileContext';
import { VenueProvider, useVenue } from '@core/contexts/VenueContext';
import { ConsentGate } from '@core/components/ConsentGate';
import { Skeleton } from '@core/components/Skeleton';
import { VenueFooter } from '@core/components/VenueFooter';
import { CoreRoutes } from '@core/routes';
import { NotFoundPage } from './pages/NotFoundPage';
import { LoginPage } from './pages/LoginPage';
import { OfflineBanner } from './components/OfflineBanner';
import { RequireAuth } from './components/RequireAuth';
import { useVenueBrandHead } from './brandHeadSync';

/**
 * Слаг заведения — первый сегмент пути (/:venueSlug/*). Читается ДО создания роутера:
 * он становится basename, и все страницы ядра навигируют слаг-независимо (как в mini-app).
 */
function getVenueSlugFromURL(): string {
  const segments = window.location.pathname.split('/').filter(Boolean);
  return segments[0] || '';
}

/** Мост pwa-auth → identity ядра: cookie-сессия даёт uuid клиента. Telegram ID у входа PWA нет и не нужен:
 * клиента сервер берёт из cookie-сессии (K-002), поэтому запросы ядра его не отправляют. */
function IdentityBridge({ children }: { children: ReactNode }) {
  const { user, status } = useAuth();
  const identity: CoreIdentity = {
    status,
    telegramId: null,
    userId: user?.id ?? null,
    firstName: user?.firstName ?? null,
  };
  return (
    <IdentityProvider value={identity}>
      <ProfileProvider>{children}</ProfileProvider>
    </IdentityProvider>
  );
}

/** Личные экраны — за гвардом сессии (anonymous → /login) и, как в мини-аппе, за согласием на обработку данных
 * (152-ФЗ): без согласия — тот же экран согласия ядра. Вход и публичный каталог открыты без них. */
const personal = (page: ReactElement) => (
  <RequireAuth>
    <ConsentGate>{page}</ConsentGate>
  </RequireAuth>
);

function VenueApp() {
  const { loading, error } = useVenue();
  useVenueBrandHead();

  if (loading) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-32" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-page">
        <div className="bg-red-50 border border-red-200 rounded-brand p-6 text-red-800 max-w-sm text-center">
          <h2 className="font-semibold mb-2 text-lg">Ошибка загрузки</h2>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page text-text-main">
      <OfflineBanner />
      {/* Экраны — общие с мини-аппом (одна карта в ядре, @core/routes): PWA получает каждый новый экран тем же
          коммитом. Своё у PWA — только вход по телефону и гвард сессии для личных экранов. */}
      <CoreRoutes personal={personal}>
        {/* Публичный вход по телефону (standalone PWA, без Telegram). */}
        <Route path="/login" element={<LoginPage />} />
      </CoreRoutes>

      <VenueFooter />

      {/* Global toast notifications */}
      <Toaster
        position="bottom-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'var(--brand-bg)',
            color: 'var(--brand-text)',
            border: '1px solid var(--brand-secondary-bg)',
            borderRadius: 'var(--brand-radius)',
          },
          success: {
            iconTheme: {
              primary: 'var(--brand-accent)',
              secondary: 'var(--brand-bg)',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: 'var(--brand-bg)',
            },
          },
        }}
      />
    </div>
  );
}

/**
 * Root '/' без venue-слага: установленная PWA стартует с start_url '/', конкретное
 * заведение — последний открытый слаг из localStorage (пишет core VenueProvider).
 * Нет сохранённого — NotFoundPage (первый вход всегда по ссылке /{slug}).
 */
function RootRedirect() {
  const lastSlug = localStorage.getItem('pwa:lastVenueSlug');
  if (lastSlug) {
    window.location.replace(`/${lastSlug}`);
    return null;
  }
  return <NotFoundPage />;
}

/**
 * Root App: слаг = basename роутера → страницы ядра живут на слаг-независимых путях
 * (точь-в-точь mini-app), а «какое заведение» решает URL, не код.
 */
export default function App() {
  const venueSlug = getVenueSlugFromURL();

  if (!venueSlug) {
    return <RootRedirect />;
  }

  return (
    <BrowserRouter basename={`/${venueSlug}`}>
      <AuthProvider>
        <IdentityBridge>
          <VenueProvider slug={venueSlug}>
            <VenueApp />
          </VenueProvider>
        </IdentityBridge>
      </AuthProvider>
    </BrowserRouter>
  );
}
